import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Share2, Loader2, Map } from 'lucide-react';
import { useHeart } from '@/context/HeartContext';
import { toast } from 'sonner';
import { getFileUrl, getStats } from '@/lib/api';
import { cityConfig } from '@/config/cities';

const Success = () => {
  const navigate = useNavigate();
  const { lastResult, setLastResult } = useHeart();
  const [sharing, setSharing] = useState(false);
  const [previewReady, setPreviewReady] = useState(null);
  const [totalDiscovered, setTotalDiscovered] = useState(null);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    getStats().then(s => setTotalDiscovered(s.total_discovered)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!lastResult) {
      const saved = localStorage.getItem('lastDiscovery');
      if (saved) {
        try { const { result } = JSON.parse(saved); setLastResult(result); return; } catch (e) { console.error('Failed to parse saved discovery:', e); }
      }
      navigate('/find', { replace: true });
    }
  }, [lastResult, navigate, setLastResult]);

  const buildCanvas = useCallback(async (result) => {
    const C = document.createElement('canvas');
    C.width = 1080; C.height = 1920;
    const x = C.getContext('2d');
    const W = C.width, H = C.height;

    // === PHOTO DU CŒUR = TOUT LE FOND ===
    const closePhotoSrc = result.close_photo_path ? getFileUrl(result.close_photo_path) : null;
    x.fillStyle = '#000000';
    x.fillRect(0, 0, W, H);

    if (closePhotoSrc) {
      try {
        const resp = await fetch(closePhotoSrc);
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        await new Promise((r, j) => { img.onload = r; img.onerror = j; img.src = blobUrl; });
        const scale = Math.max(W / img.width, H / img.height);
        const iw = img.width * scale, ih = img.height * scale;
        x.drawImage(img, (W - iw) / 2, (H - ih) / 2, iw, ih);
        URL.revokeObjectURL(blobUrl);
      } catch (e) { console.error('Photo load failed:', e); }
    }

    // === Overlay — fort en bas pour texte lisible ===
    const ov = x.createLinearGradient(0, H * 0.3, 0, H);
    ov.addColorStop(0, 'rgba(0,0,0,0)');
    ov.addColorStop(0.35, 'rgba(0,0,0,0.3)');
    ov.addColorStop(0.6, 'rgba(0,0,0,0.7)');
    ov.addColorStop(1, 'rgba(0,0,0,0.95)');
    x.fillStyle = ov; x.fillRect(0, H * 0.3, W, H * 0.7);

    // === HAUT : #326 + rang ===
    x.textAlign = 'center';
    x.shadowColor = 'rgba(255,255,255,0.3)'; x.shadowBlur = 15;

    x.fillStyle = '#000000';
    x.font = 'bold 170px sans-serif';
    x.fillText(`#${result.heart_number}`, W / 2, H * 0.19);

    // "10e sur 1000" — centré sous le numéro, une seule ligne
    if (result.explorer_rank) {
      x.fillStyle = '#000000';
      x.font = '32px sans-serif';
      x.textAlign = 'center';
      x.fillText(`${result.explorer_rank}e sur ${cityConfig.totalHearts}`, W / 2, H * 0.19 + 50);
    }

    x.shadowColor = 'transparent'; x.shadowBlur = 0;

    // === BAS : J'AI TROUVÉ / LE MIEN / ET TOI ? + ♥ HEARTS IN PARIS ===
    x.shadowColor = 'rgba(0,0,0,0.9)'; x.shadowBlur = 25;

    const baseY = H * 0.58;

    x.fillStyle = '#FFFFFF';
    x.font = 'bold 100px sans-serif';
    x.fillText("J'AI TROUVÉ", W / 2, baseY);

    x.font = 'bold 150px sans-serif';
    x.fillText('LE MIEN', W / 2, baseY + 160);

    x.fillStyle = '#E63946';
    x.font = 'bold 150px sans-serif';
    x.fillText('ET TOI ?', W / 2, baseY + 320);

    // ♥ HEARTS IN [CITY] — BLANC avec ART en rouge
    x.font = 'bold 56px sans-serif';
    const titleSuffix = cityConfig.titleSuffix || `IN ${cityConfig.name.toUpperCase()}`;
    const hipFullText = `HEARTS ${titleSuffix}`;
    const hipFullW = x.measureText(hipFullText).width;
    const hrtW = x.measureText('♥  ').width;
    const allW = hrtW + hipFullW;
    const sx = (W - allW) / 2;
    x.textAlign = 'left';
    x.fillStyle = '#E63946';
    x.fillText('♥  ', sx, baseY + 410);
    x.fillStyle = '#FFFFFF';
    const hW = x.measureText('HE').width;
    x.fillText('HE', sx + hrtW, baseY + 410);
    x.fillStyle = '#E63946';
    const aW = x.measureText('ART').width;
    x.fillText('ART', sx + hrtW + hW, baseY + 410);
    x.fillStyle = '#FFFFFF';
    const remaining = `S ${titleSuffix}`;
    x.fillText(remaining, sx + hrtW + hW + aW, baseY + 410);

    // "Xe explorateur sur Y" — blanc, petit, sous HEARTS IN PARIS, saut de ligne
    if (result.explorer_rank) {
      x.textAlign = 'center';
      x.fillStyle = 'rgba(255,255,255,0.6)';
      x.font = '30px sans-serif';
      const expText = totalDiscovered ? `${result.explorer_rank}e explorateur sur ${totalDiscovered}` : `${result.explorer_rank}e explorateur`;
      x.fillText(expText, W / 2, baseY + 500);
    }

    x.shadowColor = 'transparent'; x.shadowBlur = 0;

    return C;
  }, [totalDiscovered]);

  useEffect(() => {
    if (!lastResult) return;
    const timer = setTimeout(async () => {
      try {
        const canvas = await buildCanvas(lastResult);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        if (dataUrl && dataUrl.length > 100) setPreviewReady(dataUrl);
      } catch (e) { console.error('Preview generation failed:', e); }
    }, 500);
    return () => clearTimeout(timer);
  }, [lastResult, buildCanvas]);

  const handleShare = async () => {
    if (!lastResult) return;
    setSharing(true);
    try {
      const canvas = await buildCanvas(lastResult);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const file = new File([blob], `hearts-in-${cityConfig.id}-${lastResult.heart_number}.png`, { type: 'image/png' });

      if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `${cityConfig.title} #${lastResult.heart_number}`, text: `Cœur #${lastResult.heart_number} découvert à ${cityConfig.name} ! @qargo.art` });
        toast.success('Partagé !');
      } else {
        const link = document.createElement('a');
        link.download = `hearts-in-${cityConfig.id}-${lastResult.heart_number}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('Image sauvegardée !');
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        try {
          const canvas = await buildCanvas(lastResult);
          const link = document.createElement('a');
          link.download = `hearts-in-${cityConfig.id}-${lastResult.heart_number}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
          toast.success('Image sauvegardée !');
        } catch (e2) { console.error('Fallback share failed:', e2); }
      }
    } finally { setSharing(false); }
  };

  if (!lastResult) return null;

  return (
    <div
      className="fixed top-14 bottom-[60px] left-0 right-0 flex flex-col items-center justify-center px-4 z-30"
      style={{ background: '#000000', overflow: 'hidden', touchAction: 'none', overscrollBehavior: 'none' }}
      data-testid="success-page"
    >
      <div className="w-full flex flex-col items-center gap-3" style={{ maxWidth: '340px' }}>

        {/* Preview — centré verticalement, pas de scroll */}
        {previewReady ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="share-preview" className="w-full flex justify-center">
            <img src={previewReady} alt="Aperçu" className="w-full rounded-xl" draggable="false" style={{ maxHeight: 'calc(100dvh - 56px - 60px - 130px)', objectFit: 'contain' }} />
          </motion.div>
        ) : (
          <div className="w-full rounded-xl flex items-center justify-center" style={{ height: '300px', background: 'rgba(255,255,255,0.03)' }}>
            <Loader2 className="w-6 h-6 animate-spin text-[#E63946]" />
          </div>
        )}

        {/* Actions */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="w-full space-y-2">
          <button onClick={handleShare} disabled={sharing}
            className="w-full text-white font-semibold px-6 py-3.5 flex items-center justify-center gap-2.5 transition-all duration-300 text-sm"
            style={{ background: '#E63946', borderRadius: '14px', boxShadow: '0 8px 40px rgba(230, 57, 70, 0.35)' }}
            data-testid="share-button">
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            {sharing ? 'Préparation...' : 'Partager ma découverte'}
          </button>
          <button onClick={() => navigate('/map', { replace: true })}
            className="w-full text-white/50 font-medium px-6 py-2.5 flex items-center justify-center gap-2 transition-all text-sm hover:text-white/70"
            data-testid="go-to-map-button">
            <Map className="w-4 h-4" />
            Voir la carte
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default Success;
